// NewsEvents.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db, storage } from "../config/firebaseConfig"; // adjust path as needed
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Share2, ArrowLeft, ArrowRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { hasPermission, PERMISSIONS } from "../utils/roleAccess";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { Minimize2, Maximize2 } from "lucide-react";
/**
 * NewsEvents.jsx
 * - Tailwind CSS only
 * - Firestore: events collection
 * - localStorage caching (10 minutes)
 * - Pagination (6 items per "page" / load-more)
 * - Filters (month dropdown, date input)
 * - Role-based buttons (admin / teacher / principal)
 * - Responsive dynamic media grid (1..4)
 * - Fullscreen modal with navigation and keyboard controls
 */

const CACHE_KEY = "newsEvents_cache_v1";
const CACHE_TTL = 1000 * 60 * 10; // 10 minutes
const PAGE_SIZE = 6;

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || "").trim());
}

function isVideoUrl(value) {
  const url = String(value || "").trim();
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  return [".mp4", ".webm", ".ogg", ".mov", ".m4v"].some((ext) =>
    clean.endsWith(ext),
  );
}

async function resolveMediaUrls(rawUrls) {
  if (!Array.isArray(rawUrls) || rawUrls.length === 0) return [];

  const normalized = rawUrls
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item.url === "string") return item.url;
      return "";
    })
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  if (normalized.length === 0) return [];

  return Promise.all(
    normalized.map(async (value) => {
      if (isHttpUrl(value)) return value;
      try {
        return await getDownloadURL(storageRef(storage, value));
      } catch (error) {
        console.warn("Unable to resolve media URL:", value, error);
        return value;
      }
    }),
  );
}

function formatDateToDisplay(isoOrYmd) {
  if (!isoOrYmd) return "";
  // Support Date object, ISO string, or yyyy-mm-dd string
  const d = new Date(isoOrYmd);
  if (isNaN(d)) return isoOrYmd; // fallback
  return d.toLocaleDateString("en-GB"); // DD/MM/YYYY
}

function getEventMonth(isoOrYmd) {
  if (!isoOrYmd) return "";
  const parts = isoOrYmd.split("-");
  // expecting yyyy-mm-dd or ISO, fallback to Date
  if (parts.length >= 2) return parts[1];
  const d = new Date(isoOrYmd);
  if (isNaN(d)) return "";
  const m = d.getMonth() + 1;
  return m < 10 ? `0${m}` : `${m}`;
}

export default function NewsEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canAddEvent, setCanAddEvent] = useState(false);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showCount, setShowCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [approvalComments, setApprovalComments] = useState({});
  const [statusSelections, setStatusSelections] = useState({});
  const [reviewStatusFilter, setReviewStatusFilter] = useState("all");
  const [approvingEventId, setApprovingEventId] = useState("");
  const canApproveEvents = isAdmin;
  const approverRole = "admin";
  const normalizedSearchText = searchText.trim().toLowerCase();

  // fullscreen modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEvent, setModalEvent] = useState(null);
  const [modalIndex, setModalIndex] = useState(0);
  const modalRef = useRef(null);
  const [queueOptionsOpen, setQueueOptionsOpen] = useState({});

  // --- Auth & roles ---
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (current) => {
      if (!current) {
        setUser(null);
        setIsAdmin(false);
        setCanAddEvent(false);
        return;
      }
      setUser(current);
      try {
        const userRef = doc(db, "users", current.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          const isAdminUser = hasPermission(data, PERMISSIONS.MANAGE_EVENTS);
          setIsAdmin(isAdminUser);
          setCanAddEvent(hasPermission(data, PERMISSIONS.CREATE_EVENTS));
        } else {
          setIsAdmin(false);
          setCanAddEvent(false);
        }
      } catch (err) {
        console.error("Failed to fetch user roles:", err);
        setIsAdmin(false);
        setCanAddEvent(false);
      }
    });
    return () => unsub();
  }, []);

  // ADD THIS STATE near other states
  const [collapsedEvents, setCollapsedEvents] = useState({});

  // ADD THIS FUNCTION
  const toggleCollapse = (eventId) => {
    setCollapsedEvents((prev) => ({
      ...prev,
      [eventId]: !prev[eventId],
    }));
  };

  const shareEvent = useCallback(async (eventRecord) => {
    if (!eventRecord?.id) return;

    const shareUrl = `${window.location.origin}/events/${eventRecord.id}`;
    const title = String(eventRecord.eventName || "Event");

    if (navigator.share) {
      try {
        await navigator.share({ title, url: shareUrl });
      } catch (err) {
        // user cancelled
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      alert("Event link copied!");
    } catch (err) {
      window.prompt("Copy this link:", shareUrl);
    }
  }, []);

  // --- Fetch events with caching ---
  const fetchAndCacheEvents = useCallback(async () => {
    setLoading(true);
    try {
      const qSnap = await getDocs(collection(db, "events"));
      const allEvents = await Promise.all(
        qSnap.docs.map(async (d) => {
          const eventData = { id: d.id, ...d.data() };
          const imageUrls = await resolveMediaUrls(eventData.imageUrls);
          return { ...eventData, imageUrls };
        }),
      );

      allEvents.sort((a, b) => {
        // sort descending by date (newest first). handle missing dates
        const da = a.date || "";
        const dbb = b.date || "";
        if (!da && !dbb) return 0;
        if (!da) return 1;
        if (!dbb) return -1;
        // compare ISO strings or yyyy-mm-dd lexicographically
        return dbb.localeCompare(da);
      });
      const approvedEvents = allEvents.filter((e) => e.status === "approved");

      // store in cache
      const payload = { timestamp: Date.now(), events: approvedEvents };
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
      } catch (e) {
        // ignore localStorage errors (quota, private mode)
        console.warn("Could not save events cache:", e);
      }
      setEvents(allEvents);
      setLoading(false);
      return allEvents;
    } catch (err) {
      console.error("Error fetching events:", err);
      setLoading(false);
      return [];
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.timestamp && parsed.events) {
              const age = Date.now() - parsed.timestamp;
              if (age < CACHE_TTL) {
                const cachedEvents = (parsed.events || []).map((ev) => ({
                  ...ev,
                  status: "approved",
                }));
                if (mounted) {
                  setEvents(cachedEvents);
                }

                const needsMediaResolution = cachedEvents.some(
                  (eventRecord) =>
                    Array.isArray(eventRecord.imageUrls) &&
                    eventRecord.imageUrls.some(
                      (value) => value && !isHttpUrl(value),
                    ),
                );

                if (needsMediaResolution) {
                  Promise.all(
                    cachedEvents.map(async (eventRecord) => ({
                      ...eventRecord,
                      imageUrls: await resolveMediaUrls(eventRecord.imageUrls),
                    })),
                  )
                    .then((resolvedEvents) => {
                      if (!mounted) return;
                      setEvents(resolvedEvents);
                      try {
                        localStorage.setItem(
                          CACHE_KEY,
                          JSON.stringify({
                            timestamp: parsed.timestamp || Date.now(),
                            events: resolvedEvents,
                          }),
                        );
                      } catch (e) {
                        console.warn("Could not update events cache:", e);
                      }
                    })
                    .catch((error) => {
                      console.warn(
                        "Failed to resolve cached event media URLs:",
                        error,
                      );
                    });
                }
                // optionally refresh in background if older than half TTL? skipping per spec
                return;
              }
            }
          } catch (e) {
            console.warn("Invalid cache, refetching.");
          }
        }
        // fetch if no valid cache
        if (mounted) await fetchAndCacheEvents();
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [fetchAndCacheEvents]);

  useEffect(() => {
    if (!canApproveEvents) {
      return;
    }

    fetchAndCacheEvents();
  }, [canApproveEvents, fetchAndCacheEvents]);

  const matchesSearchQuery = useCallback(
    (eventRecord) => {
      if (!normalizedSearchText) {
        return true;
      }

      const searchableText = [
        eventRecord.eventName,
        eventRecord.description,
        eventRecord.date,
        eventRecord.status,
        eventRecord.approvalComment,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedSearchText);
    },
    [normalizedSearchText],
  );

  const approvedEvents = useMemo(
    () => events.filter((ev) => ev.status === "approved"),
    [events],
  );

  const searchedApprovedEvents = useMemo(
    () => approvedEvents.filter(matchesSearchQuery),
    [approvedEvents, matchesSearchQuery],
  );

  const reviewableEvents = useMemo(() => {
    let out = events.filter(matchesSearchQuery);

    if (filterMonth) {
      out = out.filter(
        (eventRecord) => getEventMonth(eventRecord.date) === filterMonth,
      );
    }

    if (filterDate) {
      out = out.filter((eventRecord) => eventRecord.date === filterDate);
    }

    if (reviewStatusFilter !== "all") {
      out = out.filter(
        (eventRecord) =>
          String(eventRecord.status || "wip").toLowerCase() ===
          reviewStatusFilter,
      );
    }

    return out;
  }, [events, matchesSearchQuery, filterMonth, filterDate, reviewStatusFilter]);

  // --- Derived months for filter dropdown ---
  const months = useMemo(() => {
    const sourceEvents = canApproveEvents ? events : approvedEvents;
    const setMonths = new Set(
      sourceEvents.map((ev) => getEventMonth(ev.date)).filter(Boolean),
    );
    return Array.from(setMonths).sort();
  }, [canApproveEvents, events, approvedEvents]);

  // --- Filtering behavior ---
  useEffect(() => {
    let out = searchedApprovedEvents.slice();
    if (filterMonth)
      out = out.filter((ev) => getEventMonth(ev.date) === filterMonth);
    if (filterDate)
      out = out.filter((ev) => {
        // input type="date" returns yyyy-mm-dd
        return ev.date === filterDate;
      });
    setFiltered(out);
    // reset pagination when filters change
    setShowCount(PAGE_SIZE);
  }, [searchedApprovedEvents, filterMonth, filterDate]);

  // --- Pagination handlers ---
  const loadMore = () => {
    setShowCount((s) => s + PAGE_SIZE);
  };

  // --- Modal / fullscreen handlers ---
  const openModalAt = (eventObj, startIndex = 0) => {
    setModalEvent(eventObj);
    setModalIndex(startIndex);
    setModalOpen(true);
    // focus the modal container when opened
    setTimeout(() => modalRef.current?.focus(), 10);
    // lock body scroll
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalEvent(null);
    setModalIndex(0);
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  };

  const goPrev = () => {
    if (!modalEvent) return;
    setModalIndex((i) => {
      const len = modalEvent.imageUrls?.length || 0;
      return (i - 1 + len) % len;
    });
  };

  const goNext = () => {
    if (!modalEvent) return;
    setModalIndex((i) => {
      const len = modalEvent.imageUrls?.length || 0;
      return (i + 1) % len;
    });
  };

  // keyboard navigation
  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, modalEvent]);

  // --- helpers for grid classes based on number of media items shown ---
  const gridClassesForCount = (count) => {
    // 1 -> single hero (16:9)
    // 2 -> two columns
    // 3 or 4 -> 2x2 responsive
    if (count === 1) return "grid-cols-1";
    if (count === 2) return "grid-cols-2 gap-2";
    return "grid-cols-2 gap-2";
  };

  // defensive: ensure events is an array
  const displayEvents = filtered.slice(0, showCount);

  const handleEventStatusUpdate = async (eventRecord) => {
    const eventId = eventRecord.id;
    const nextStatus = String(
      statusSelections[eventId] || eventRecord.status || "wip",
    )
      .trim()
      .toLowerCase();
    const comment = String(approvalComments[eventId] || "").trim();

    setApprovingEventId(eventId);

    try {
      const updatePayload = {
        status: nextStatus,
        approvedByRole: approverRole,
        approvedByUid: user?.uid || "",
        updatedAt: Timestamp.now(),
      };

      if (nextStatus === "approved") {
        updatePayload.approvedAt = Timestamp.now();
      }

      if (comment) {
        updatePayload.approvalComment = comment;
      }

      await updateDoc(doc(db, "events", eventId), updatePayload);

      setEvents((currentEvents) =>
        currentEvents.map((eventRecord) =>
          eventRecord.id === eventId
            ? {
                ...eventRecord,
                status: nextStatus,
                ...(comment ? { approvalComment: comment } : {}),
                approvedByRole: approverRole,
                approvedByUid: user?.uid || "",
                ...(nextStatus === "approved"
                  ? { approvedAt: new Date().toISOString() }
                  : {}),
              }
            : eventRecord,
        ),
      );

      setApprovalComments((currentValue) => {
        const nextValue = { ...currentValue };
        delete nextValue[eventId];
        return nextValue;
      });
      setStatusSelections((currentValue) => {
        const nextValue = { ...currentValue };
        delete nextValue[eventId];
        return nextValue;
      });
      setQueueOptionsOpen((currentValue) => ({
        ...currentValue,
        [eventId]: false,
      }));
      alert(`Event status updated to ${nextStatus}.`);
    } catch (error) {
      alert(`Failed to update event status: ${error.message}`);
    } finally {
      setApprovingEventId("");
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-indigo-100 to-slate-200">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* top controls */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-3 items-center">
            {canAddEvent && (
              <button
                onClick={() => navigate("/add-event")}
                className="bg-gradient-to-r from-indigo-500 to-blue-500 text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg hover:scale-[1.03] transition-all"
                aria-label="Add Event"
              >
                Add New Event
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => navigate("/manage-events")}
                className="bg-white/80 border border-white/40 text-gray-800 px-4 py-2 rounded-full shadow-sm hover:shadow-md transition"
                aria-label="Manage Events"
              >
                Manage Events
              </button>
            )}
            <div className="text-sm text-gray-700">
              {loading
                ? "Loading events..."
                : canApproveEvents
                  ? `${filtered.length} matching approved events | ${Math.max(events.length - approvedEvents.length, 0)} pending/review events`
                  : `${filtered.length} matching approved events`}
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3 items-end">
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600">
                Search Events
              </label>
              <input
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search by title, description, date, status"
                className="mt-1 min-w-[260px] px-3 py-2 rounded-lg border border-white/40 bg-white/70 backdrop-blur-sm text-sm focus:outline-none"
                aria-label="Search events globally"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600">
                Filter by Month
              </label>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="mt-1 px-3 py-2 rounded-lg border border-white/40 bg-white/70 backdrop-blur-sm text-sm focus:outline-none"
                aria-label="Filter by month"
              >
                <option value="">All months</option>
                {months.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600">
                Filter by Date
              </label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="mt-1 px-3 py-2 rounded-lg border border-white/40 bg-white/70 backdrop-blur-sm text-sm focus:outline-none"
                aria-label="Filter by date"
              />
            </div>

            {(filterMonth || filterDate || searchText.trim()) && (
              <button
                onClick={() => {
                  setSearchText("");
                  setFilterMonth("");
                  setFilterDate("");
                }}
                className="px-3 py-2 rounded-lg bg-white/60 border border-white/30 hover:bg-white transition text-sm"
                aria-label="Clear filters"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {canApproveEvents && (
          <section className="rounded-2xl border border-indigo-200 bg-white/70 p-4 shadow-lg backdrop-blur-md">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-indigo-900">
                  Event Status Queue
                </h2>
                <p className="text-sm text-indigo-700">
                  Review any event and update status. Comment is optional.
                </p>
              </div>

              <div className="flex flex-col">
                <label className="text-xs font-semibold text-indigo-700">
                  Filter Queue By Status
                </label>
                <select
                  value={reviewStatusFilter}
                  onChange={(event) =>
                    setReviewStatusFilter(event.target.value)
                  }
                  className="mt-1 min-w-[190px] rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-indigo-400"
                >
                  <option value="all">All statuses</option>
                  <option value="approved">Approved</option>
                  <option value="wip">Work In Progress</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {reviewableEvents.length === 0 ? (
              <div className="rounded-xl border border-dashed border-indigo-200 bg-white/70 p-4 text-sm text-indigo-700">
                No events available for status update.
              </div>
            ) : (
              <div className="space-y-4">
                {reviewableEvents.map((eventRecord) => {
                  return (
                    <article
                      key={eventRecord.id}
                      className="rounded-xl border border-white/50 bg-white/80 p-4 shadow-sm"
                    >
                      {/* HEADER */}
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-base font-semibold text-slate-800">
                          {eventRecord.eventName}
                        </h3>

                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                            {eventRecord.status || "wip"}
                          </span>
                          <button
                            onClick={() => shareEvent(eventRecord)}
                            className="p-2 rounded-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition"
                            aria-label={`Share ${eventRecord.eventName}`}
                            title="Share event"
                          >
                            <Share2 size={16} />
                          </button>
                          {/* ✅ MINIMIZE BUTTON */}
                          <button
                            onClick={() => toggleCollapse(eventRecord.id)}
                            className="p-2 rounded-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition"
                            aria-label={
                              collapsedEvents[eventRecord.id]
                                ? "Expand event"
                                : "Minimize event"
                            }
                          >
                            {collapsedEvents[eventRecord.id] ? (
                              <Maximize2 size={16} />
                            ) : (
                              <Minimize2 size={16} />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* COLLAPSIBLE CONTENT */}
                      {!collapsedEvents[eventRecord.id] && (
                        <>
                          <p className="mt-1 text-xs text-slate-500">
                            {formatDateToDisplay(eventRecord.date)}
                          </p>

                          <p className="mt-2 text-sm text-slate-700">
                            {eventRecord.description || "No description"}
                          </p>

                          {/* ✅ SHOW ALL MEDIA (NO LIMIT) */}
                          <div className="mt-3 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                            {(eventRecord.imageUrls || []).map((url, idx) => (
                              <button
                                key={idx}
                                onClick={() => openModalAt(eventRecord, idx)}
                                className="aspect-square overflow-hidden rounded-xl"
                              >
                                {isVideoUrl(url) ? (
                                  <video
                                    src={url}
                                    className="w-full h-full object-cover"
                                    muted
                                    playsInline
                                  />
                                ) : (
                                  <img
                                    src={url}
                                    alt={`${eventRecord.eventName || "Event"} media ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                )}
                              </button>
                            ))}
                          </div>

                          {/* STATUS CONTROLS */}
                          <div className="grid gap-3 sm:grid-cols-[0.45fr_0.55fr] mt-3">
                            <div>
                              <label className="text-xs font-semibold text-slate-600">
                                Status
                              </label>
                              <select
                                value={
                                  statusSelections[eventRecord.id] ||
                                  eventRecord.status ||
                                  "wip"
                                }
                                onChange={(e) =>
                                  setStatusSelections((prev) => ({
                                    ...prev,
                                    [eventRecord.id]: e.target.value,
                                  }))
                                }
                                className="w-full mt-1 rounded-lg border px-3 py-2 text-sm"
                              >
                                <option value="wip">Work In Progress</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-xs font-semibold text-slate-600">
                                Comment
                              </label>
                              <textarea
                                value={approvalComments[eventRecord.id] || ""}
                                onChange={(e) =>
                                  setApprovalComments((prev) => ({
                                    ...prev,
                                    [eventRecord.id]: e.target.value,
                                  }))
                                }
                                rows={2}
                                className="w-full mt-1 rounded-lg border px-3 py-2 text-sm"
                              />
                            </div>
                          </div>

                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={() =>
                                handleEventStatusUpdate(eventRecord)
                              }
                              disabled={approvingEventId === eventRecord.id}
                              className="rounded-full bg-green-500 px-4 py-2 text-white text-sm"
                            >
                              {approvingEventId === eventRecord.id
                                ? "Updating..."
                                : "Update Status"}
                            </button>
                          </div>
                        </>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {!canApproveEvents && (
          <>
            {/* Events grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayEvents.length === 0 && (
                <div className="col-span-full text-center text-gray-600 p-6 bg-white/70 backdrop-blur-md rounded-2xl border border-white/30 shadow-sm">
                  No events found.
                </div>
              )}

              {displayEvents.map((ev) => {
                const media = Array.isArray(ev.imageUrls) ? ev.imageUrls : [];
                // show up to 4 thumbnails in the card (with a "show more" inside modal)
                const thumbs = media.slice(0, 4);
                const count = thumbs.length;

                // dynamic aspect classes:
                const singleClass = "aspect-[16/9]"; // hero
                const squareClass = "aspect-square";

                return (
                  <article
                    key={ev.id}
                    className="bg-white/70 backdrop-blur-md rounded-2xl shadow-lg border border-white/30 p-4 hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
                    aria-labelledby={`title-${ev.id}`}
                  >
                    <header className="mb-2">
                      <h3
                        id={`title-${ev.id}`}
                        className="text-lg font-semibold text-gray-800"
                      >
                        {ev.eventName}
                      </h3>
                      <div className="text-sm text-gray-500">
                        {formatDateToDisplay(ev.date)}
                      </div>
                    </header>

                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {ev.description || ""}
                    </p>

                    {/* media grid */}
                    <div className={`grid ${gridClassesForCount(count)} mb-3`}>
                      {count === 0 && (
                        <div className="p-6 rounded-xl bg-white/60 border border-white/30 text-center text-sm text-gray-600">
                          No media uploaded
                        </div>
                      )}

                      {thumbs.map((url, idx) => {
                        const isSingle = thumbs.length === 1;
                        const aspectClass = isSingle
                          ? singleClass
                          : squareClass;

                        return (
                          <button
                            key={idx}
                            onClick={() => openModalAt(ev, idx)}
                            className={`relative overflow-hidden rounded-xl ${aspectClass} focus:outline-none`}
                            aria-label={`Open media ${idx + 1} for ${ev.eventName}`}
                          >
                            {isVideoUrl(url) ? (
                              <video
                                src={url}
                                className="w-full h-full object-cover"
                                preload="metadata"
                                muted
                                playsInline
                              />
                            ) : (
                              <img
                                src={url}
                                alt={`${ev.eventName}-${idx}`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            )}

                            {/* if there are more than 4 media, overlay a count on the last thumb */}
                            {media.length > 4 && idx === thumbs.length - 1 && (
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center text-white font-semibold text-lg">
                                +{media.length - thumbs.length}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <div className="flex gap-3 items-center justify-between">
                      <button
                        onClick={() => shareEvent(ev)}
                        className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-500 text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all text-sm"
                        aria-label={`Share ${ev.eventName}`}
                      >
                        <Share2 size={16} />
                        <span>Share Event</span>
                      </button>

                      <div className="text-xs text-gray-500">
                        Status: approved
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Load more */}
            {filtered.length > showCount && (
              <div className="text-center">
                <button
                  onClick={loadMore}
                  className="px-6 py-2 rounded-full bg-white/80 border border-white/30 shadow-sm hover:shadow-md transition"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Fullscreen modal */}
      {modalOpen && modalEvent && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 backdrop-blur-md overflow-y-auto p-4"
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-6xl mx-auto flex flex-col items-center"
          >
            {/* Close button (always visible) */}
            <button
              onClick={closeModal}
              className="fixed top-4 right-4 z-50 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition"
              aria-label="Close media viewer"
            >
              <X size={20} />
            </button>

            {/* Navigation arrows */}
            {modalEvent.imageUrls && modalEvent.imageUrls.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goPrev();
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-md hover:scale-105 transition"
                  aria-label="Previous"
                >
                  <ArrowLeft size={20} />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goNext();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-md hover:scale-105 transition"
                  aria-label="Next"
                >
                  <ArrowRight size={20} />
                </button>
              </>
            )}

            {/* Center media */}
            <div className="w-full flex justify-center mt-12 mb-6">
              {modalEvent.imageUrls && modalEvent.imageUrls.length > 0 ? (
                <div className="max-h-[80vh] max-w-[95vw] flex items-center justify-center">
                  {isVideoUrl(modalEvent.imageUrls[modalIndex]) ? (
                    <video
                      src={modalEvent.imageUrls[modalIndex]}
                      controls
                      autoPlay
                      playsInline
                      className="max-h-[80vh] max-w-[95vw] rounded-2xl object-contain"
                    />
                  ) : (
                    <img
                      src={modalEvent.imageUrls[modalIndex]}
                      alt={`${modalEvent.eventName}-${modalIndex}`}
                      className="max-h-[80vh] max-w-[95vw] rounded-2xl object-contain"
                    />
                  )}
                </div>
              ) : (
                <div className="p-8 bg-white/80 rounded-2xl">
                  No media available
                </div>
              )}
            </div>

            {/* Caption
            <div className="text-center text-white/90 mb-4">
              <h3 className="text-lg font-semibold">{modalEvent.eventName}</h3>
              <div className="text-sm text-white/80">
                {formatDateToDisplay(modalEvent.date)}
              </div>
              {modalEvent.description && (
                <p className="text-sm text-white/70 mt-2 px-4 max-w-3xl mx-auto">
                  {modalEvent.description}
                </p>
              )}
            </div> */}

            {/* Thumbnail strip (always visible) */}
            {modalEvent.imageUrls && modalEvent.imageUrls.length > 1 && (
              <div className="w-full max-w-4xl overflow-x-auto py-3 bg-black/30 rounded-lg">
                <div className="flex gap-3 px-2 items-center justify-center">
                  {modalEvent.imageUrls.map((u, i) => {
                    return (
                      <button
                        key={i}
                        onClick={() => setModalIndex(i)}
                        className={`flex-shrink-0 w-15 h-10 rounded-md overflow-hidden border-2 ${
                          i === modalIndex ? "border-white" : "border-white/30"
                        }`}
                        aria-label={`Thumbnail ${i + 1}`}
                      >
                        {isVideoUrl(u) ? (
                          <video
                            src={u}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                          />
                        ) : (
                          <img
                            src={u}
                            alt={`thumb-${i}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
